// src/shared/changelog/request/request.ts
import type { AxiosInstance } from "axios";

import type { ChangelogEntry } from "../types";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

/**
 * Absolute on purpose. `useAxiosAuth0` pins its `baseURL` to `…/v1`, and this
 * endpoint is v2 — `/api/v1/changelog` is already the Activity Log, so the
 * changelog could not reuse that prefix (CTX-199). An absolute URL overrides
 * the instance's base without giving up the Auth0 token interceptor or the
 * 401 → `/login` redirect that comes with it, which is why this does not
 * follow the other v2 request modules' habit of calling bare `axios` with a
 * hand-passed token.
 */
const CHANGELOG_URL = `${API_BASE_URL}/v2/changelog`;

/**
 * The server's page size cap. Asking for more is silently clamped, so this
 * mirrors it rather than pretending a larger page is available.
 */
const MAX_LIMIT = 100;

interface ChangelogResponse {
  success: boolean;
  message: string;
  data: ChangelogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Every entry the tenant is entitled to see, newest first.
 *
 * The server applies both halves of the visibility rule — products against the
 * tenant's entitlement, audiences against its type — and only returns entries a
 * successful production deploy published. The client re-filters nothing: that
 * rule lived in two places during phase 1 by design, and CTX-202 removes the
 * copy so the two cannot drift.
 *
 * Pages are walked to exhaustion rather than exposed to the caller. The feed is
 * ~50 entries growing at ~5/month, so "all of it" is one or two requests and an
 * infinite-scroll affordance would be machinery for a problem nobody has. If
 * the volume ever justifies it, this is the single place that changes.
 */
export async function fetchChangelogEntries(
  client: AxiosInstance,
): Promise<ChangelogEntry[]> {
  const entries: ChangelogEntry[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const { data } = await client.get<ChangelogResponse>(CHANGELOG_URL, {
      params: { page, limit: MAX_LIMIT },
    });
    entries.push(...(data.data ?? []));
    totalPages = data.pagination?.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages);

  return entries;
}
